// src/components/Navbar/Sidebar.jsx
// Menú lateral para móvil/tablet (< lg). Mismos links que el Navbar de escritorio
// (ver navItems.js), con foto grande, sección de cuenta, idioma e Instagram.
import { Dialog, DialogBackdrop, DialogPanel } from "@headlessui/react";
import {
  XMarkIcon,
  ChevronRightIcon,
  UserCircleIcon,
  Squares2X2Icon,
  ArrowRightStartOnRectangleIcon,
  HomeIcon,
} from "@heroicons/react/24/outline";
import { Link, NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "flag-icons/css/flag-icons.min.css";
import { PUBLIC_NAV_ITEMS, ACCOUNT_NAV_ITEMS, INSTAGRAM_URL } from "./navItems";
import logo from "./logo.jpeg";

const SIDEBAR_IMAGE =
  "https://res.cloudinary.com/dc6xthiol/image/upload/w_800,q_auto,f_auto/v1743405483/20241227_172600_skquzn.jpg";

const LANGUAGES = [
  { code: "es", label: "ES", countryCode: "mx" },
  { code: "en", label: "EN", countryCode: "us" },
];

function SidebarLink({ item, onNavigate, index }) {
  const { t } = useTranslation();
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.to === "/"}
      onClick={onNavigate}
      style={{ animationDelay: `${60 + index * 35}ms` }}
      className={({ isActive }) =>
        `sidebar-item group flex items-center gap-4 rounded-2xl px-3 py-3 transition-colors duration-150 active:scale-[0.98] ${
          isActive ? "bg-[#9A0002] text-[#EFE6DD]" : "text-[#3A2A20] hover:bg-[#9A0002]/5"
        }`
      }
    >
      {({ isActive }) => (
        <>
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${
              isActive ? "bg-[#EFE6DD]/15" : "bg-white shadow-sm ring-1 ring-[#3A2A20]/5"
            }`}
          >
            <Icon className={`h-5 w-5 ${isActive ? "text-[#EFE6DD]" : "text-[#9A0002]"}`} aria-hidden="true" />
          </span>
          <span className="flex-1 text-[17px] font-medium">{t(item.labelKey) || item.fallback}</span>
          <ChevronRightIcon
            className={`h-4 w-4 ${isActive ? "text-[#EFE6DD]/70" : "text-[#3A2A20]/25"}`}
            aria-hidden="true"
          />
        </>
      )}
    </NavLink>
  );
}

export default function Sidebar({ open, onClose, user, isLoggedIn, isAdmin, onLogout }) {
  const { t, i18n } = useTranslation();
  const firstName = user?.fullname?.split(" ")[0];

  const exploreItems = [{ to: "/", labelKey: "navbar.home", fallback: "Home", icon: HomeIcon }, ...PUBLIC_NAV_ITEMS];
  const accountItems = [
    ...ACCOUNT_NAV_ITEMS,
    { to: "/profile", labelKey: "navbar.myProfile", fallback: "My Profile", icon: UserCircleIcon },
    ...(isAdmin
      ? [{ to: "/admin", labelKey: "navbar.adminDashboard", fallback: "Admin", icon: Squares2X2Icon }]
      : []),
  ];

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50 lg:hidden">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-[#1f0a0a]/50 backdrop-blur-[2px] transition-opacity duration-300 ease-out data-[closed]:opacity-0"
      />

      <div className="fixed inset-0 flex">
        <DialogPanel
          transition
          className="relative flex h-full w-[88vw] max-w-sm flex-col overflow-hidden bg-[#EFE6DD] shadow-2xl transition-transform duration-300 ease-[cubic-bezier(0.32,0.72,0,1)] data-[closed]:-translate-x-full"
        >
          {/* Cabecera con foto grande */}
          <div className="relative h-56 shrink-0 overflow-hidden">
            <img src={SIDEBAR_IMAGE} alt="" className="absolute inset-0 h-full w-full object-cover object-bottom" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#7A0001] via-[#9A0002]/60 to-black/10" />

            <button
              type="button"
              onClick={onClose}
              className="absolute right-3 top-[max(0.75rem,env(safe-area-inset-top))] flex h-10 w-10 items-center justify-center rounded-full bg-black/25 text-white backdrop-blur-md transition active:scale-95"
            >
              <span className="sr-only">{t("navbar.closeMenu")}</span>
              <XMarkIcon className="h-6 w-6" aria-hidden="true" />
            </button>

            <Link to="/" onClick={onClose} className="absolute bottom-5 left-5 right-5 flex items-center gap-3">
              <div className="h-14 w-14 shrink-0 overflow-hidden rounded-full border-2 border-[#EFE6DD] bg-white shadow-lg">
                <img className="h-16 w-auto object-contain object-top" src={logo} alt="Hotel Macarios" />
              </div>
              <div className="min-w-0 text-[#EFE6DD]">
                <p className="font-['Fraunces'] text-2xl font-semibold leading-tight">Hotel Macarios</p>
                {isLoggedIn && firstName && (
                  <p className="truncate text-sm text-[#EFE6DD]/80">
                    {t("navbar.greeting", { name: firstName, defaultValue: `Hi, ${firstName}` })}
                  </p>
                )}
              </div>
            </Link>
          </div>

          {/* Links */}
          <nav className="flex-1 overflow-y-auto overscroll-contain px-3 pb-6 pt-5">
            <p className="px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9A0002]/70">
              {t("navbar.explore", "Explore")}
            </p>
            <div className="space-y-1">
              {exploreItems.map((item, i) => (
                <SidebarLink key={item.to} item={item} index={i} onNavigate={onClose} />
              ))}
            </div>

            {isLoggedIn && (
              <>
                <p className="mt-6 px-3 pb-2 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9A0002]/70">
                  {t("navbar.account", "My account")}
                </p>
                <div className="space-y-1">
                  {accountItems.map((item, i) => (
                    <SidebarLink
                      key={item.to}
                      item={item}
                      index={exploreItems.length + i}
                      onNavigate={onClose}
                    />
                  ))}
                </div>
              </>
            )}

            {!isLoggedIn && (
              <div className="mt-6 grid grid-cols-2 gap-3 px-1">
                <Link
                  to="/register"
                  onClick={onClose}
                  className="rounded-2xl bg-[#9A0002] px-4 py-3.5 text-center text-sm font-semibold text-[#EFE6DD] shadow-sm transition active:scale-[0.97]"
                >
                  {t("navbar.createAccount")}
                </Link>
                <Link
                  to="/login"
                  onClick={onClose}
                  className="rounded-2xl border border-[#9A0002]/25 bg-white px-4 py-3.5 text-center text-sm font-semibold text-[#9A0002] transition active:scale-[0.97]"
                >
                  {t("navbar.signIn")}
                </Link>
              </div>
            )}
          </nav>

          {/* Pie: idioma, Instagram y cerrar sesión */}
          <div className="shrink-0 border-t border-[#3A2A20]/10 bg-white/50 px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex rounded-full bg-[#3A2A20]/5 p-1" role="group" aria-label="Language">
                {LANGUAGES.map((lang) => {
                  const active = i18n.language?.startsWith(lang.code);
                  return (
                    <button
                      key={lang.code}
                      type="button"
                      onClick={() => i18n.changeLanguage(lang.code)}
                      aria-pressed={active}
                      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                        active ? "bg-white text-[#9A0002] shadow-sm" : "text-[#3A2A20]/60"
                      }`}
                    >
                      <span className={`fi fi-${lang.countryCode} fis rounded-full`} />
                      {lang.label}
                    </button>
                  );
                })}
              </div>

              <div className="flex items-center gap-1">
                <a
                  href={INSTAGRAM_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex h-10 w-10 items-center justify-center rounded-full text-[#3A2A20]/60 transition hover:bg-[#3A2A20]/5 active:scale-95"
                >
                  <span className="sr-only">Instagram</span>
                  <svg fill="currentColor" viewBox="0 0 24 24" className="h-5 w-5" aria-hidden="true">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
                  </svg>
                </a>
                {isLoggedIn && (
                  <button
                    type="button"
                    onClick={() => {
                      onClose();
                      onLogout();
                    }}
                    className="flex h-10 items-center gap-1.5 rounded-full px-3 text-sm font-medium text-[#9A0002] transition hover:bg-[#9A0002]/5 active:scale-95"
                  >
                    <ArrowRightStartOnRectangleIcon className="h-5 w-5" aria-hidden="true" />
                    {t("navbar.signOut")}
                  </button>
                )}
              </div>
            </div>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
